/**
 * Google Publisher Tag (GPT) utilities for Google Ad Manager integration
 * Same implementation as Pickem frontend - DraftKings Tagging Architecture
 */

import type { ResponsiveSizeMapping } from '@/config/ads.config';

/**
 * What GAM reports about the creative it served: the identifiers ad-ops need to pull
 * the actual asset out of Google Ad Manager.
 *
 * Every field is optional because GPT omits them for an empty slot, and the
 * source-agnostic pair is null unless the line item came through a backfill/exchange
 * path — so a report that shows one but not the other is normal, not a bug.
 */
export interface SlotResponseInformation {
  advertiserId?: number | null;
  campaignId?: number | null;
  creativeId?: number | null;
  lineItemId?: number | null;
  sourceAgnosticCreativeId?: number | null;
  sourceAgnosticLineItemId?: number | null;
}

/**
 * GPT's `slotRenderEnded`, fired once per slot per request — including when the
 * request came back with nothing, which is the only way to distinguish "no ad was
 * sold for this slot" from "the ad has not arrived yet".
 *
 * `size` is the decisive field for the anchored-footer dispute: it is the frame GAM
 * chose from the sizes the slot offered, straight from Google rather than inferred
 * from a screenshot. It is `[width, height]` for a normal creative, a string for a
 * fluid one, and null/absent when the slot came back empty.
 */
export interface SlotRenderEndedEvent {
  slot: {
    getSlotElementId: () => string;
    getAdUnitPath?: () => string;
    getResponseInformation?: () => SlotResponseInformation | null;
  };
  isEmpty: boolean;
  size?: [number, number] | string | null;
  advertiserId?: number | null;
  campaignId?: number | null;
  creativeId?: number | null;
  lineItemId?: number | null;
  sourceAgnosticCreativeId?: number | null;
  sourceAgnosticLineItemId?: number | null;
  isBackfill?: boolean;
}

declare global {
  interface Window {
    googletag: {
      cmd: unknown[];
      apiReady?: boolean;
      defineSlot: (
        path: string,
        sizes: [number, number][],
        divId: string
      ) => {
        addService: (pubads: unknown) => void;
        defineSizeMapping: (m: unknown) => void;
        setTargeting: (k: string, v: string) => void;
      };
      pubads: () => {
        enableSingleRequest: () => void;
        collapseEmptyDivs: () => void;
        getSlots: () => { getSlotElementId: () => string }[];
        refresh: (slots: unknown[]) => void;
        addEventListener: (
          event: 'slotRenderEnded',
          listener: (event: SlotRenderEndedEvent) => void
        ) => void;
        removeEventListener: (
          event: 'slotRenderEnded',
          listener: (event: SlotRenderEndedEvent) => void
        ) => void;
      };
      sizeMapping: () => {
        addSize: (vp: [number, number], sizes: [number, number][]) => void;
        build: () => unknown;
      };
      enableServices: () => void;
      display: (divId: string) => void;
      destroySlots: (slots: unknown[]) => void;
    };
  }
}

export const initializeGPT = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.googletag && window.googletag.apiReady) {
      resolve();
      return;
    }
    window.googletag = window.googletag || { cmd: [] };
    let attempts = 0;
    const maxAttempts = 200;
    const checkGPT = () => {
      attempts++;
      if (window.googletag && window.googletag.apiReady) {
        resolve();
        return;
      }
      if (attempts >= maxAttempts) {
        reject(new Error('Timeout waiting for GPT script to load'));
        return;
      }
      setTimeout(checkGPT, 50);
    };
    setTimeout(checkGPT, 100);
  });
};

export interface AdSlotConfig {
  adUnitPath: string;
  sizes: [number, number][];
  divId: string;
  targeting?: Record<string, string>;
  sizeMappings?: ResponsiveSizeMapping[];
}

export const buildSizeMapping = (sizeMappings: ResponsiveSizeMapping[]): unknown => {
  if (!window.googletag) return null;
  const mapping = window.googletag.sizeMapping();
  const sorted = [...sizeMappings].sort((a, b) => b.viewport[0] - a.viewport[0]);
  sorted.forEach(({ viewport, sizes }) => mapping.addSize(viewport, sizes));
  return mapping.build();
};

/**
 * What each live slot was defined with, keyed by div id.
 *
 * Kept because `slotRenderEnded` reports the size GAM *chose* but not the sizes it
 * chose from, and the interesting comparison is exactly those two together: a slot
 * that offered [320x50] and filled 320x50 is working as designed, whereas the same
 * fill from a slot that also offered 320x100 means something quite different.
 */
const definedSlotConfigs = new Map<string, AdSlotConfig>();

/** The config a slot was defined with, or undefined once it has been destroyed. */
export const getDefinedSlotConfig = (divId: string): AdSlotConfig | undefined =>
  definedSlotConfigs.get(divId);

export const defineAdSlot = (config: AdSlotConfig): void => {
  if (!window.googletag) return;
  definedSlotConfigs.set(config.divId, config);
  window.googletag.cmd.push(() => {
    const slot = window.googletag.defineSlot(config.adUnitPath, config.sizes, config.divId);
    if (config.sizeMappings && config.sizeMappings.length > 0) {
      const mapping = buildSizeMapping(config.sizeMappings);
      if (mapping) slot.defineSizeMapping(mapping);
    }
    slot.addService(window.googletag.pubads());
    if (config.targeting) {
      Object.entries(config.targeting).forEach(([k, v]) => slot.setTargeting(k, v));
    }
  });
};

export const enableAndDisplayAds = (): void => {
  if (!window.googletag) return;
  window.googletag.cmd.push(() => {
    window.googletag.pubads().enableSingleRequest();
    window.googletag.pubads().collapseEmptyDivs();
    window.googletag.enableServices();
  });
};

export const displayAdSlot = (divId: string): void => {
  if (!window.googletag) return;
  window.googletag.cmd.push(() => window.googletag.display(divId));
};

/**
 * Subscribe to one slot's render outcome. Returns an unsubscribe function.
 *
 * Both the subscribe and unsubscribe go through `googletag.cmd`, which is the only
 * safe way to touch pubads: the queue is drained once GPT is live, so a caller that
 * mounts before the library loads still gets its listener attached — and a caller
 * that unmounts in between still gets it removed, in order.
 */
export const onSlotRenderEnded = (
  divId: string,
  handler: (isEmpty: boolean, event: SlotRenderEndedEvent) => void
): (() => void) => {
  if (!window.googletag) return () => {};

  const listener = (event: SlotRenderEndedEvent) => {
    if (event.slot?.getSlotElementId() === divId) handler(event.isEmpty, event);
  };

  window.googletag.cmd.push(() =>
    window.googletag.pubads().addEventListener('slotRenderEnded', listener)
  );

  return () => {
    if (!window.googletag) return;
    window.googletag.cmd.push(() =>
      window.googletag.pubads().removeEventListener('slotRenderEnded', listener)
    );
  };
};

/**
 * Subscribe to every slot's render outcome, with the full GPT event attached.
 *
 * Separate from `onSlotRenderEnded` above, which is per-slot and deliberately narrow
 * (a call site only wants to know whether its own slot went unfilled). This one is
 * for diagnostics that need the whole picture — which size GAM chose, and which
 * creative it belongs to — across every slot on the page.
 */
export const onAnySlotRenderEnded = (
  handler: (event: SlotRenderEndedEvent) => void
): (() => void) => {
  if (!window.googletag) return () => {};

  const listener = (event: SlotRenderEndedEvent) => handler(event);

  window.googletag.cmd.push(() =>
    window.googletag.pubads().addEventListener('slotRenderEnded', listener)
  );

  return () => {
    if (!window.googletag) return;
    window.googletag.cmd.push(() =>
      window.googletag.pubads().removeEventListener('slotRenderEnded', listener)
    );
  };
};

export const destroyAdSlot = (divId: string): void => {
  if (!window.googletag) return;
  definedSlotConfigs.delete(divId);
  window.googletag.cmd.push(() => {
    const slots = window.googletag.pubads().getSlots();
    const slot = slots.find(
      (s: { getSlotElementId: () => string }) => s.getSlotElementId() === divId
    );
    if (slot) window.googletag.destroySlots([slot]);
  });
};
